import { computed, watch } from 'vue';
import { useColorMode } from '@vueuse/core';

/**
 * Вибір теми користувачем. `system` іде за налаштуванням ОС, `light` / `dark`
 * закріплюють вигляд. Типова тема — темна.
 *
 * Перенесено з DOTT (`apps/app/src/shared/composables/useTheme.ts`).
 */
export type ThemePreference = 'system' | 'light' | 'dark';

// Той самий ключ читає вбудований скрипт в index.html — міняти разом.
const STORAGE_KEY = 'ugi-color-mode';

// Колір рамки браузера (адресний рядок на телефоні, шапка PWA). Це колір
// оболонки — той самий, що тло <html> у `styles/style.css`.
const THEME_COLORS: Record<'light' | 'dark', string> = {
  light: 'lch(95.2% 0.6 282)',
  dark: 'lch(2.595% 0.4 272)',
};

// Вбудований color-mode Nuxt UI вимкнено (`colorMode: false` у vite.config.ts),
// тож `useColorMode` із VueUse ведемо самі. Екземпляр один на модуль: меню
// користувача і запуск застосунку ділять один стан і один набір ефектів у DOM.
//
// `emitAuto` лишає `system` повноцінним значенням (`'auto'`), а не зводить його
// до вже обчисленого light/dark. `initialValue: 'dark'` робить перший візит
// темним — як `class="dark"` в index.html.
const colorMode = useColorMode({
  storageKey: STORAGE_KEY,
  initialValue: 'dark',
  emitAuto: true,
});

watch(
  () => colorMode.state.value,
  (resolved) => {
    const meta = document.querySelector('meta[name="theme-color"]');

    meta?.setAttribute('content', THEME_COLORS[resolved === 'dark' ? 'dark' : 'light']);
  },
  { immediate: true },
);

const preference = computed<ThemePreference>({
  get() {
    return colorMode.value === 'auto' ? 'system' : (colorMode.value as ThemePreference);
  },
  set(value) {
    colorMode.value = value === 'system' ? 'auto' : value;
  },
});

export const useTheme = () => {
  const isDark = computed(() => colorMode.state.value === 'dark');

  return {
    preference,
    isDark,
  };
};
